import React from 'react';
import { Button } from '@mui/material';
import { getPolyglot } from '../../i18n';
import { STModal } from '../commons/Modal';
import ComposableDeviceForm from './ComposableDeviceForm';
import { useMemo, useState } from 'react';
import ButtonLoading from '../commons/ButtonLoading/ButtonLoading';
import { GroupCreateAPI } from '../../redux/groups/api/group.model';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { postGroup } from '../../redux/groups/actions/thunks';
import { useAppDispatch, useAppSelector } from '../../redux/store.model';
import { useShowErrorMessage, useShowMessage } from '../../util/hooks';
import { IconUpload } from '../../redux/groups/groups.model';
import { getMachinesTypeSelector } from '../../redux/composableDevice/selectors/composableDeviceSelectors';

interface ComposableDeviceProps {
  closeAddComposableDevice: () => void;
  showAddComposableDevice: boolean;
  groupNameParent?: string;
  groupIdParent?: string;
}
export interface ComposableDeviceValues extends GroupCreateAPI {
  imageURL: string;
  logoURL: string;
}
function ComposableDeviceAdd({
  showAddComposableDevice,
  closeAddComposableDevice,
  groupNameParent,
  groupIdParent,
}: ComposableDeviceProps) {
  const polyglot = getPolyglot();

  const dispatch = useAppDispatch();

  const showError = useShowErrorMessage();
  const showSuccess = useShowMessage();
  const machineTypes = useAppSelector(getMachinesTypeSelector);
  const [logo, setLogo] = useState<IconUpload | null>(null);
  const [image, setImage] = useState<IconUpload | null>(null);

  const initialValues: ComposableDeviceValues = {
    name: '',
    parentGroupID: groupIdParent ?? '',
    composableDevice: {
      machineTypeId: machineTypes[0].id,
    },
    imageURL: '',
    logoURL: '',
    devices: [],
  };
  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .required(polyglot.t('composable_device.name_required')),
        devices: yup
          .array()
          .of(yup.string())
          .min(1, polyglot.t('composable_device.devices_required'))
          .required(polyglot.t('composable_device.devices_required')),
      }),
    [polyglot]
  );

  const [newValidationSchema, setNewValidationSchema] =
    useState(validationSchema);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: newValidationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(
          postGroup(
            values,
            logo ? logo : undefined,
            groupIdParent ? values.parentGroupID ?? '' : undefined
          )
        );
        showSuccess(polyglot.t('composable_device.save_successful_message'));
      } catch (error: any) {
        showError(polyglot.t('composable_device.failed_to_create'));
      } finally {
        closeAddComposableDevice();
      }
    },
  });

  return (
    <div>
      <STModal
        open={showAddComposableDevice}
        fullWidth
        maxWidth="sm"
        onClose={closeAddComposableDevice}
        title={
          groupNameParent
            ? `${polyglot.t(
                'composable_device.add_composable_device'
              )} - ${groupNameParent}`
            : polyglot.t('composable_device.add_composable_device')
        }
        buttonActions={
          <>
            <Button
              type="button"
              onClick={closeAddComposableDevice}
              variant="text"
            >
              {polyglot.t('device.cancel_button_title')}
            </Button>
            <ButtonLoading
              type="submit"
              variant="contained"
              onClick={formik.submitForm}
              loading={formik.isSubmitting}
            >
              {polyglot.t('device.add_button_title')}
            </ButtonLoading>
          </>
        }
      >
        <div>
          <ComposableDeviceForm
            groupNameParent={groupNameParent}
            formik={formik}
            setValidationSchema={setNewValidationSchema}
            validationSchema={validationSchema}
            logo={logo}
            setLogo={setLogo}
            image={image}
            setImage={setImage}
          />
        </div>
      </STModal>
    </div>
  );
}

export default ComposableDeviceAdd;
